import { AfterContentChecked, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { AuthResponse, AuthService, UserInfo } from './auth/auth.service';
import { UtilitieService } from './services/utils/utilities.service';
import { WebsocketService } from './services/utils/websocket.service';
import { catchError, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from './payment/payment.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy, AfterContentChecked {

  title = 'Yard Hostle';
  @ViewChild('sideMenu') sideNav: MatSidenav;
  isOnline = signal<boolean>(false);
  loading = signal<boolean>(false);
  isLoggedIn = signal<boolean>(false);
  isGuest = signal<boolean>(false);
  isConnected = signal<boolean>(false);
  activeInstance = signal<boolean>(false);

  // Reactive role state

  roleSignal = signal<string>('');


  /** Used to clean up the subscription */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private utilService: UtilitieService,
    private webSocketService: WebsocketService,
    private zone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private payService: PaymentService,
    private cdr: ChangeDetectorRef
  ) {

  }


  logOut() {
    // instance stored in token
    this.authService.logout();
    this.webSocketService.disconnect();
    this.isLoggedIn.set(false);
  }

  incomingSideMenuEvent(task: string) {
    switch (task) {
      case 'logout':
        this.logOut();
        break;
      case 'login':
        this.openLoginDialog();
        break;
      default:
        break;
    }
    this.sideNav.toggle();
  }

  incomingMainMenuEvent(task: string) {
    switch (task) {
      case 'logout':
        this.logOut();
        break;
      case 'login':
        this.openLoginDialog();
        break;
      default:
        this.sideNav.toggle();
        break;
    }
  }

  openLoginDialog() {
    this.authService.openLoginDialog();
  }

  testURLOpen() {

    const testUrl = 'https://beerswift.app/tabs/tab2';
    console.log('Testing URL open:', testUrl);

    this.zone.run(() => {
      const slug = testUrl.split('.app').pop();
      if (slug) {
        this.router.navigateByUrl(slug);
      }
    });
  }

  /*
  Order sequence
  1) The user will login.
  2) Then the user will be propteted for which instance to be in.
  3) The instance data will then be loaded.
   */
  async ngOnInit() {
    this.loading.set(true);
    this.authService.listenToInternetConnection();
    this.isGuest.set(false);

    const authResponse = await this.authService.checkAccountStatus();
    if (authResponse) {
      await this.authService.updateAuthState(authResponse);
    }

    await this.utilService.displaySiteNotifications();
    const promo = await this.route.snapshot.queryParamMap.get('promo');
    if (promo) {
      this.payService.addPromoCode(promo);
    }

    this.utilService.getLoadingState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(loadState => {
        this.loading.set(loadState);
      });

    this.authService.getOnlineState()
      .pipe(
        switchMap<boolean, Observable<AuthResponse>>(isOnline => {
          this.isOnline.set(isOnline);
          return this.authService.getAuthState();
        }),
        switchMap<AuthResponse, Observable<boolean>>(authResponse => {
          this.isLoggedIn.set(authResponse.authenticated !== null && authResponse.authenticated === true);
          return this.webSocketService.checkConnection();
        }),
        takeUntil(this.destroy$),
        catchError(err => {
          return of(false);
        }))
      .subscribe({
        next: async (isConnected: boolean) => {
          this.activeInstance.set(false);
          this.isConnected.set(isConnected);

          if (this.isLoggedIn()) {

            const authResponse: AuthResponse = await this.authService.getAuthResponse();
            this.isGuest.set(authResponse.guest);
            this.roleSignal.set(authResponse.role);
            let instance = authResponse.instance;
            if (authResponse.member) {
              instance = authResponse.instance;
            } else {
              instance = await this.authService.getActiveInstance();
            }
            if (!this.isConnected() && instance) {
              await this.webSocketService.connect();
            }

            if (isConnected && instance) {
              this.activeInstance.set(true);
              this.webSocketService.listenToUpdates(instance, authResponse.chatId);
            }
          } else {
            this.isConnected.set(false);
            this.activeInstance.set(false);
            this.webSocketService.disconnect();
          }

        }
      });

    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.zone.run(() => {
          // Example url: https://beerswift.app/tabs/tab2
          // slug = /tabs/tab2
          const slug = event.url.split(".com").pop();
          if (slug) {
            this.router.navigateByUrl(slug);
          }
          // If no match, do nothing - let regular routing
          // logic take over
        });
      });
    }
    
  }

  ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  async ngOnDestroy(): Promise<void> {
    this.webSocketService.disconnect();
    this.authService.logout();
    this.destroy$.next();
    this.destroy$.complete();
  }

}
